import { useEffect, useState } from 'react';

import { words } from '@karehero/helpers/string';

/** Without a clause ending, words are checked this long after the last check. */
export const LIVE_CHECK_INTERVAL_MS = 2000;

const LAST_CLAUSE_BREAK = /[,.;:?!][^,.;:?!]*$/;

/** The words alone, so a change of punctuation, case or spacing is not checked again. */
export const wordsOf = (text: string) => words(text.toLowerCase()).join(' ');

/** The words up to the last clause ending; once these change, a check is due at once. */
export const clausedWordsOf = (text: string) => {
  const end = text.search(LAST_CLAUSE_BREAK);
  return end < 0 ? '' : wordsOf(text.slice(0, end + 1));
};

/** Whether the words now are the words before with more added. */
const isAddingTo = (before: string, now: string) =>
  !!before && now.startsWith(before);

/** What was last sent to be checked, so nothing is checked twice or too soon. */
interface Sent {
  text: string;
  words: string;
  clauses: string;
  at: number;
}

const NOTHING_SENT: Readonly<Sent> = {
  text: '',
  words: '',
  clauses: '',
  at: 0,
};

/** How long until the next check is due: at once on a clause ending, else the rest of the gap. */
export const checkDueIn = (
  sent: Readonly<Sent>,
  clauses: string,
  now: number,
) =>
  clauses !== sent.clauses
    ? 0
    : Math.max(0, sent.at + LIVE_CHECK_INTERVAL_MS - now);

/** What is shown, and the words it was found in. */
interface Found<T> {
  value: T;
  words: string;
}

/** A result for the words sent: built on what is shown while words were only added, else on its own. */
export const withResult = <T>(
  found: Found<T>,
  sent: string,
  value: T,
  keepFurthest: (shown: T, found: T) => T,
): Found<T> => ({
  value: isAddingTo(found.words, sent)
    ? keepFurthest(found.value, value)
    : value,
  words: sent,
});

export interface UseLiveCheckOptions<T> {
  /** What has been said or typed so far, live words included. */
  text: string;
  isEnabled: boolean;
  /** What is shown before anything is said, and once it is all taken back. */
  initial: T;
  /** Keep stable: a new one sends any check in flight again. */
  check: (text: string) => Promise<T>;
  /** Folds a result for more words into what is shown, so it does not flicker back. */
  keepFurthest: (shown: T, found: T) => T;
}

export interface LiveCheck<T> {
  value: T;
  /** Whether the words as they stand have been checked, so what is shown is all there is to come. */
  isCaughtUp: boolean;
}

/**
 * useLiveCheck checks words as they come: as soon as a clause ends, else a
 * few seconds after the last check, only when the words have changed, and
 * one check at a time. While words are only added, results build on each
 * other; once any are changed, the next result stands on its own, and once
 * they are taken back, the check in flight is abandoned. With no words left,
 * initial is shown at once.
 */
export const useLiveCheck = <T>({
  text,
  isEnabled,
  initial,
  check,
  keepFurthest,
}: UseLiveCheckOptions<T>): LiveCheck<T> => {
  const [found, setFound] = useState<Found<T>>({ value: initial, words: '' });
  const [sent, setSent] = useState(NOTHING_SENT);
  const [isChecking, setIsChecking] = useState(false);
  // Set once the check has ended, after any result, so caught up never comes before what was found.
  const [checkedWords, setCheckedWords] = useState('');
  const words = wordsOf(text);
  const clauses = clausedWordsOf(text);
  const isTakenBack = !words.startsWith(sent.words);

  // Nothing said: initial at once, and nothing counts as sent.
  useEffect(() => {
    if (words) return;
    setFound({ value: initial, words: '' });
    setSent((s) => ({ ...NOTHING_SENT, at: s.at }));
  }, [initial, words]);

  useEffect(() => {
    if (isChecking && isTakenBack) setIsChecking(false);
  }, [isChecking, isTakenBack]);

  // Due as soon as a clause ends, else once the gap since the last check has passed.
  useEffect(() => {
    if (!isEnabled || !words || isChecking || words === sent.words) return;
    const timer = setTimeout(
      () => {
        setSent({ text, words, clauses, at: Date.now() });
        setIsChecking(true);
      },
      checkDueIn(sent, clauses, Date.now()),
    );
    return () => clearTimeout(timer);
  }, [clauses, isChecking, isEnabled, sent, text, words]);

  useEffect(() => {
    if (!isChecking) return;
    let isCurrent = true;
    check(sent.text.trim())
      .then((value) => {
        if (!isCurrent) return;
        setFound((f) => withResult(f, sent.words, value, keepFurthest));
      })
      // Best effort: nothing ticks, and the API reports its own failures.
      .catch(() => undefined)
      .finally(() => {
        if (!isCurrent) return;
        setIsChecking(false);
        setCheckedWords(sent.words);
      });
    return () => {
      isCurrent = false;
    };
  }, [check, isChecking, keepFurthest, sent]);

  return {
    value: found.value,
    isCaughtUp: !isEnabled || !words || checkedWords === words,
  };
};
